import { CampaignType, TYPE_ICONS } from 'cio/models/campaign';
import { FilterMessage } from 'cio/services/filters';
import { Channel } from 'cio/utils/metrics/common';
import { any } from 'cio/app-models/conditions/action';
import actionInfo from 'cio/utils/action-info';
import { ActionType } from 'cio/models/action';
import { capitalize } from '@ember/string';
import { sortByCaseInsensitive } from '../sort';

export interface DropdownMessage extends Partial<FilterMessage> {
  output_id: string;
  hideIcon?: boolean;
  isDeleted?: boolean;
  isAny?: boolean;
  isAnyInParent?: boolean;
  isTopLevelWildcard?: boolean;
  isGroupWildcard?: boolean;
  isGroup?: boolean;
  showLeftOffset?: boolean;
  split?: boolean;
}

export interface DropdownGroup {
  isGroup: true;
  isTopLevelGroup: boolean;
  showBottomBorder?: boolean;
  groupName: string;
  groupType: 'campaign' | 'broadcast' | 'newsletter' | 'transactional message' | CampaignType;
  icon: string;
  options: (DropdownMessage | DropdownGroup)[];
}

function isDropdownMessage(obj: any): obj is DropdownMessage {
  return !isDropdownGroup(obj);
}

function isDropdownGroup(obj: any): obj is DropdownGroup {
  return obj.isGroup != null && obj.isGroup;
}

const GROUP_SORT_ORDER = ['campaign', 'newsletter', 'broadcast', 'transactional message'];

export function orderMessages(
  messages: FilterMessage[],
  currentId: string | null,
  isDeleted: boolean,
  allowWildcardSelection: boolean,
  allowInParentWildcardSelection: boolean,
  type: Channel,
) {
  let info = actionInfo(`${type}_action` as ActionType);
  let anyId = any(type);

  let orderedMessages: DropdownMessage[] = [];

  if (type === 'email' || allowWildcardSelection) {
    orderedMessages.push({
      type,
      subject: `any ${info.shortName}`,
      searchLabel: `any ${info.shortName}`,
      isAny: true,
      isTopLevelWildcard: true,
      hideIcon: true,
      output_id: anyId,
      parent: {
        id: '',
        type: 'any',
        name: 'in any campaign, newsletter, or transactional message',
      },
    });
  }

  let items: DropdownMessage[] = [];
  let wildcards: Record<string, boolean> = {};

  messages.forEach((item) => {
    if (item.parent?.type === 'campaign' || item.parent?.type === 'broadcast') {
      let id = `c_${item.campaign_id}`;
      if (allowInParentWildcardSelection && wildcards[id] == null) {
        items.push({
          type,
          subject: `any ${info.shortName}`,
          searchLabel: `any ${info.shortName} in ${item.parent.name}`,
          isAny: true,
          isAnyInParent: true,
          isGroupWildcard: true,
          hideIcon: true,
          output_id: `any_${type}-campaign_${item.campaign_id}`,
          parent: { ...item.parent },
        });
        wildcards[id] = true;
      }
    }

    items.push(item);
  });

  items = items.sort((a, b) => {
    if (a.parent != null && b.parent != null && a.parent?.type === b.parent?.type) {
      if (a.parent.id === b.parent.id) {
        if (a.isGroupWildcard && !b.isGroupWildcard) {
          return -1;
        }

        if (!a.isGroupWildcard && b.isGroupWildcard) {
          return 1;
        }

        if (a.parent.type === 'newsletter') {
          if (a.can_segment_on_variation) {
            // Sorts the "All variations" item before the variations, and then
            // the variations in A, B, C etc order
            if (a.is_segmentable_variation && !b.is_segmentable_variation) {
              return 1;
            }

            if (!a.is_segmentable_variation && b.is_segmentable_variation) {
              return -1;
            }

            return (a.variation ?? 0) - (b.variation ?? 0);
          }
        }

        if (a.sort_value === b.sort_value) {
          if (a.isLanguageBranch && !b.isLanguageBranch) {
            return -1;
          }

          if (!a.isLanguageBranch && b.isLanguageBranch) {
            return 1;
          }
        }

        let subjectSortValue = a.subject?.localeCompare(b.subject ?? '', undefined, { sensitivity: 'accent' });
        let languageSortValue = a.language?.localeCompare(b.language ?? '', undefined, { sensitivity: 'accent' });

        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        return subjectSortValue || languageSortValue || 0;
      }

      return a.parent.name.localeCompare(b.parent.name, undefined, { sensitivity: 'accent' });
    }

    if (a.parent != null && b.parent != null) {
      return GROUP_SORT_ORDER.indexOf(a.parent.type) - GROUP_SORT_ORDER.indexOf(b.parent.type);
    }

    return 0;
  });

  orderedMessages.push(...items);

  if (currentId != null && isDeleted) {
    orderedMessages.unshiftObject({
      type,
      subject: `${capitalize(info.shortName)} Deleted`,
      isDeleted: true,
      output_id: currentId,
    });
  }

  return orderedMessages;
}

export function groupMessages(messages: DropdownMessage[]) {
  return Object.values(
    messages.reduce<Record<string, any>>((acc, item) => {
      if ((item.isDeleted ?? false) || (item.isTopLevelWildcard ?? false)) {
        acc[item.output_id] = item;
        return acc;
      }

      let showLeftOffset = true;
      if (item.variation != null && item.is_segmentable_variation) {
        showLeftOffset = false;
      }
      let itemClone = {
        ...item,
        showLeftOffset,
        hideIcon: true,
        hideParent: !item.isGroupWildcard ?? false,
      };

      if (item.parent?.type === 'newsletter') {
        if (acc.newsletters == null) {
          acc.newsletters = {
            groupName: 'Newsletters',
            groupType: item.parent.type,
            isTopLevelGroup: true,
            isGroup: true,
            options: [],
            groups: {},
          };
        }

        let id = `n_${item.parent.id}`;
        if (acc.newsletters.groups[id] == null) {
          acc.newsletters.groups[id] = {
            groupName: item.parent.name,
            groupType: item.parent.type,
            isGroup: true,
            icon: 'newsletter',
            options: [],
          };
        }

        acc.newsletters.groups[id].options.push(itemClone);
      } else if (item.parent?.type === 'campaign' || item.parent?.type === 'broadcast') {
        if (acc[item.parent.type] == null) {
          acc[item.parent.type] = {
            groupName: item.parent.type === 'campaign' ? 'Campaigns' : 'API Triggered Broadcasts',
            groupType: item.parent.type,
            isTopLevelGroup: true,
            isGroup: true,
            options: [],
            groups: {},
          };
        }

        let id = `c_${item.parent.id}`;
        if (acc[item.parent?.type]?.groups[id] == null) {
          acc[item.parent.type].groups[id] = {
            groupName: item.parent.name,
            groupType: item.parent.type,
            isGroup: true,
            icon: item.parent?.campaignType != null ? TYPE_ICONS[item.parent.campaignType as CampaignType] : '',
            options: [],
          };
        }

        acc[item.parent.type].groups[id].options.push(itemClone);
      } else if (item.parent?.type === 'transactional message') {
        if (acc.transactional == null) {
          acc.transactional = {
            groupName: 'Transactional messages',
            groupType: item.parent.type,
            isTopLevelGroup: true,
            showBottomBorder: true,
            isGroup: true,
            options: [],
          };
        }
        itemClone.hideParent = true;
        acc.transactional.options.push(itemClone);
      }

      return acc;
    }, {}),
  )
    .reduce((acc, item) => {
      if (item.isDeleted || item.isTopLevelWildcard || item.groupType === 'transactional message') {
        acc.push(item);
        return acc;
      }

      let groupItem = { ...item };
      groupItem.options = sortByCaseInsensitive(Object.values(item.groups) as DropdownGroup[], ['groupName']);
      delete groupItem.groups;

      acc.push(groupItem);
      return acc;
    }, [])
    .sort((a: DropdownMessage | DropdownGroup, b: DropdownMessage | DropdownGroup) => {
      if (isDropdownGroup(a) && isDropdownGroup(b)) {
        return GROUP_SORT_ORDER.indexOf(a.groupType) - GROUP_SORT_ORDER.indexOf(b.groupType);
      }

      if (isDropdownMessage(a) && a.isDeleted) {
        if (isDropdownMessage(b) && b.isTopLevelWildcard) {
          return 1;
        }

        return -1;
      }
      if (isDropdownMessage(b) && b.isDeleted) {
        if (isDropdownMessage(a) && a.isTopLevelWildcard) {
          return -1;
        }

        return 1;
      }

      if (isDropdownMessage(a) && a.isTopLevelWildcard) {
        return -1;
      }

      if (isDropdownMessage(b) && b.isTopLevelWildcard) {
        return 1;
      }

      return 0;
    });
}
